/**
 * Partner accent colours are arbitrary user input, so they are validated and
 * their contrast is computed rather than trusted.
 *
 * A partner picking pale yellow must not be able to make text unreadable, so
 * the accent is only ever used where contrast is either irrelevant (a
 * decorative bar) or actively resolved (readableOn picks the foreground).
 */

/** #rgb or #rrggbb, normalised to #rrggbb. Null if it isn't a colour. */
export function parseAccent(value: string | null | undefined): string | null {
	if (!value) return null;
	const v = value.trim();
	if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
	if (/^#[0-9a-f]{3}$/i.test(v)) {
		const [r, g, b] = [v[1], v[2], v[3]];
		return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
	}
	return null;
}

/** WCAG relative luminance. */
function luminance(hex: string): number {
	const c = [1, 3, 5].map((i) => {
		const ch = Number.parseInt(hex.slice(i, i + 2), 16) / 255;
		return ch <= 0.03928 ? ch / 12.92 : ((ch + 0.055) / 1.055) ** 2.4;
	}) as [number, number, number];
	return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/**
 * The more legible of Curo's two text colours on the given background.
 *
 * Compares actual contrast rather than guessing a luminance threshold — a
 * threshold gets mid-tones wrong, and mid-tones are exactly where legibility
 * breaks. Near-black rather than pure black, so it still reads as Curo.
 */
export function readableOn(hex: string): string {
	const dark = "#272727";
	const light = "#ffffff";
	return contrastRatio(hex, dark) >= contrastRatio(hex, light) ? dark : light;
}

/** Contrast ratio between two colours, per WCAG. */
export function contrastRatio(a: string, b: string): number {
	const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [
		number,
		number,
	];
	return (l1 + 0.05) / (l2 + 0.05);
}

/** WCAG AA for body text. */
const AA = 4.5;

/**
 * Whether text can sit on this colour at all.
 *
 * Some colours cannot: a mid-grey like #808080 reaches only 3.95 against white
 * and 3.78 against near-black, so NEITHER foreground is legible on it. That is
 * a property of the colour, not a bug to work around — which is why a partner
 * accent is used decoratively (see BrandBar) and never as a text background
 * unless this returns true.
 */
export function canCarryText(hex: string): boolean {
	return contrastRatio(hex, readableOn(hex)) >= AA;
}
